import { useEffect, useMemo, useState } from "react";
import { socket } from "../lib/socket";
import { useAuth } from './Auth/AuthContext';
import '../assets/styles/attendanceMonitor.css';
import { API_BASE_URL } from "../config/api";

const SCAN_STEPS = [
  { key: "RFID_READ", label: "RFID read" },
  { key: "BACKEND_RECEIVED", label: "Backend received" },
  { key: "MEMBER_LOOKUP", label: "Member lookup" },
  { key: "ATTENDANCE_LOGGED", label: "Attendance logged" },
  { key: "COMPLETE", label: "Complete" },
];

const getStepStatus= (stepUpdate) => {
    if (!stepUpdate) return "pending";
    // if (stepUpdate.status === "success") return "success";
    // if (stepUpdate.status === "failed") return "failed";
    return stepUpdate.status;
}

const getOverallStatus = (scan) => {
    if(!scan.steps) return "processing";
    const stepValues = Object.values(scan.steps);
    if(stepValues.some((s) => s.status === "failed")) return "failed";
    if(scan.steps["COMPLETE"]?.status === "success") return "complete";
    return "processing";
}

const formatTime = (timestamp) => {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getInitials = (name) => {
  if (!name) return "?";
  return name.slice(0, 2).toUpperCase();
};

const getUserName = async (memberCode, token) => {
    const response = await fetch(`${API_BASE_URL}/attendance/users/member-code/${memberCode}`, {
        'headers': {
            'Authorization': `Bearer ${token}`
        }
    })

    if(!response.ok) {
        throw new Error('Network response was not ok');
    }

    const data = await response.json();
    const fullName = data.Name;
    const initials = getInitials(fullName);
        
    return {
        "name" : fullName,
        "initials" : initials
    };
}

const fillClassMap = {
  success: "fillSuccess",
  failed: "fillFailed",
  processing: "fillActive",
  pending: "fillPending",
};

const StepBar = ({ stepUpdate }) => {
    const status = getStepStatus(stepUpdate);
    return (
        <div className="barBg">
            <div className={`barFill ${fillClassMap[status]}`}></div>
        </div>
    )
}

const ScanCard = ({ scan }) => {
    const { token } = useAuth();
    const [userNameInfo, setUserNameInfo] = useState({ name : "", initials : "?" });
    const status = getOverallStatus(scan);
    const time = formatTime(scan.updatedAt || scan.createdAt);

    useEffect(() => {
        if(!scan.memberCode) {
            return;
        }

        getUserName(scan.memberCode, token)
            .then(setUserNameInfo)
            .catch(() => setUserNameInfo({ name: "Unknown member", initials: "?" }));
    }, [scan.memberCode, token])

    const cardStatusClass = {
        complete: "cardComplete",
        failed: "cardFailed",
        processing: "cardProcessing"
    }[status];

    const badgeStatusClass = {
        complete: "badgeComplete",
        failed: "badgeFailed",
        processing: "badgeProcessing",
    }[status];

    const badgeText = {
        complete: "Complete",
        failed: "Failed",
        processing: "Processing",
    }[status];

    return (
        <div className={`card ${cardStatusClass}`}>
            <div className="cardTop">
                <div
                className={`avatar ${status === "failed" ? "avatarFailed" : ""} ${
                    userNameInfo.initials === "?" ? "avatarUnknown" : ""
                }`}
                >
                {userNameInfo.initials}
                </div>
                <div className="cardInfo">
                <div className="cardName">{userNameInfo.name || "Unknown member"}</div>
                <div className="cardDevice">
                    {scan.deviceID || "Unknown device"} · {scan.scanID?.slice(0, 8)}
                </div>
                </div>
                <div className="cardRight">
                <span className="cardTime">{time}</span>
                <span className={`badge ${badgeStatusClass}`}>{badgeText}</span>
                </div>
            </div>

            <div className="stepsRow">
                {SCAN_STEPS.map((step) => {
                const stepUpdate = scan.steps?.[step.key];
                return (
                    <div key={step.key} className="step">
                    <div className="stepLabel">{step.label}</div>
                    <StepBar stepUpdate={stepUpdate} />
                    </div>
                );
                })}
            </div>

            {scan.latestMessage && status !== "complete" && (
                <div
                className={`message ${
                    status === "failed" ? "messageFailed" : "messageInfo"
                }`}
                >
                {scan.latestMessage}
                </div>
            )}
        </div>
    );
}

const AttendanceMonitor = () => {
    const { token } = useAuth();
    const [connected, setConnected] = useState(socket.connected);
    const [scans, setScans] = useState({});
    // const [activeSessionID, setActiveSessionID] = useState(null);

    // const handleCreateAndStartSession = async () => {
    //     try {
    //         const createResponse = await fetch(`${API_BASE_URL}/attendance/create-session`, {
    //             method: "POST",
    //             headers: {
    //                 Authorization: `Bearer ${token}`,
    //             },
    //         });

    //         const created = await createResponse.json();

    //         if (!createResponse.ok) {
    //             throw new Error(created.error || "Failed to create session");
    //         }

    //         const sessionID = created.sessionId;

    //         const startResponse = await fetch(
    //             `${API_BASE_URL}/attendance/start-session/${sessionID}`,
    //                 {
    //                     method: "POST",
    //                     headers: {
    //                     Authorization: `Bearer ${token}`,
    //                     },
    //                 }
    //             );

    //             const started = await startResponse.json();

    //             if (!startResponse.ok) {
    //                 throw new Error(started.error || "Failed to start session");
    //             }

    //             setActiveSessionID(sessionID);

    //             console.log("Session created and started:", sessionID);
    //         } catch (err) {
    //             console.error("Failed to create/start session:", err.message);
    //         }
    //     };

    //     const handleStopSession = async() => {
    //         try {
    //             const response = await fetch(
    //                 `${API_BASE_URL}/attendance/end-session`,
    //                     {
    //                         method: 'POST',
    //                         headers: {
    //                             'Content-Type': 'application/json',
    //                             'Authorization': `Bearer ${token}`
    //                         },
    //                     }
    //             );

    //             const data = await response.json();

    //             if (!response.ok) {
    //                 throw new Error(data.error || "Failed to stop session");
    //             }

    //             setActiveSessionID(null);
    //         } catch(err) {
    //             console.error("Failed to stop session:", err.message);
    //         }
    //     }

    useEffect(() => {
        const handleConnect = () => {
            setConnected(true);
            console.log("Socket connected: ", socket.id);
        }

        const handleDisconnect = (reason) => {
            setConnected(false);
            console.log("Socket disconnected: ", reason);
        }

        const handleScanUpdate = (update) => {
            console.log("scan:update", update);
            if(!update.scanID || !update.step) {
                console.warn("Invalid scan update: ", update);
                return;
            }

            const resolvedStatus =
                update.status === "success" ? "success" :
                update.status === "failed"  ? "failed"  :
                "processing";

            setScans((prev) => {
                const existingScan = prev[update.scanID] || {
                    scanID: update.scanID,
                    deviceID: update.deviceID,
                    memberCode: update.memberCode,
                    sessionID: update.sessionID,
                    steps: {},
                    createdAt: update.timestamp
                };

                return {
                    ...prev,
                    [update.scanID]: {
                        ...existingScan,
                        deviceID: update.deviceID || existingScan.deviceID,
                        memberCode: update.memberCode || existingScan.memberCode,
                        sessionID: update.sessionID || existingScan.sessionID,
                        steps: {
                        ...existingScan.steps,
                        [update.step]: {
                            ...update,
                            status: resolvedStatus,  // always write the normalized status
                        },
                        },
                        latestStatus: resolvedStatus,
                        latestMessage: update.message,
                        latestErrorCode: update.errorCode,
                        updatedAt: update.timestamp,
                    },
                };
            })
        }

        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.on("scan:update", handleScanUpdate);

        socket.connect();

        return () => {
            socket.off("connect", handleConnect);
            socket.off("disconnect", handleDisconnect);
            socket.off("scan:update", handleScanUpdate);
            socket.disconnect();
        }
    }, []);

    const scanList = useMemo(() => {
        return Object.values(scans).sort(
        (a, b) =>
            new Date(b.updatedAt || b.createdAt) -
            new Date(a.updatedAt || a.createdAt)
        );
    }, [scans]);

    const stats = useMemo(() => {
        const total = scanList.length;
        const successful = scanList.filter(
        (s) => getOverallStatus(s) === "complete"
        ).length;
        const failed = scanList.filter(
        (s) => getOverallStatus(s) === "failed"
        ).length;
        const processing = scanList.filter(
        (s) => getOverallStatus(s) === "processing"
        ).length;
        return { total, successful, failed, processing };
    }, [scanList]);

    return (
        <div className="monitor">
        <div className="statsRow">
            <div className="stat">
            <div className="statLabel">Total scans</div>
            <div className="statVal statValBlue">{stats.total}</div>
            </div>
            <div className="stat">
            <div className="statLabel">Successful</div>
            <div className="statVal statValGreen">{stats.successful}</div>
            </div>
            <div className="stat">
            <div className="statLabel">Failed</div>
            <div className="statVal statValRed">{stats.failed}</div>
            </div>
            <div className="stat">
            <div className="statLabel">Processing</div>
            <div className="statVal statValAmber">{stats.processing}</div>
            </div>
        </div>

        {/* <button onClick={() => {handleCreateAndStartSession()}}>Start Session</button>
        <button onClick={() => {handleStopSession()}}>Stop Session</button> */}


        <div className="feedLabel">
            <span
            className={`liveDot ${connected ? "" : "liveDotDisconnected"}`}
            />
            Live scan feed
            {!connected && (
            <span className="disconnectedLabel">· Disconnected</span>
            )}
        </div>

        <div>
            {scanList.length === 0 ? (
            <div className="empty">Waiting for scans...</div>
            ) : (
            scanList.map((scan) => <ScanCard key={scan.scanID} scan={scan} />)
            )}
        </div>
        </div>
    );
}

export default AttendanceMonitor;